import { useEffect, useRef, useState } from "react";

interface GoogleCredentialResponse {
  credential: string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback?: (response: GoogleCredentialResponse) => void;
            use_fedcm_for_button?: boolean;
            auto_select?: boolean;
            ux_mode?: "popup" | "redirect";
            login_uri?: string;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              theme?: "outline" | "filled_blue" | "filled_black";
              size?: "large" | "medium" | "small";
              text?: "signin_with" | "signup_with" | "continue_with" | "signin";
              shape?: "rectangular" | "pill" | "circle" | "square";
              width?: number;
              locale?: string;
            },
          ) => void;
        };
      };
    };
  }
}

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const API_URL = import.meta.env.VITE_API_URL;
const LOGIN_URI = API_URL ? `${API_URL}/api/auth/google-redirect` : undefined;

interface Props {
  /**
   * Unused in redirect mode (Google does a full-page redirect to LOGIN_URI
   * instead of invoking a JS callback). Kept for backward compatibility with
   * any popup-mode callsites — if we ever go back to popup we re-wire it.
   */
  onCredential?: (credential: string) => void;
  width?: number;
}

/**
 * Renders Google's official "Continue with Google" button via Google Identity
 * Services. The script tag is loaded in index.html; this component polls for
 * `window.google` to appear, then initializes and renders into a ref'd div.
 *
 * The handler is read from a ref so React re-renders don't force a re-init —
 * GSI's init is idempotent but renderButton paints fresh HTML every time.
 */
export function GoogleLoginButton({ onCredential, width = 280 }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const callbackRef = useRef(onCredential);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    callbackRef.current = onCredential;
  }, [onCredential]);

  useEffect(() => {
    if (!CLIENT_ID) return;
    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      if (window.google?.accounts?.id) setReady(true);
      else setTimeout(check, 100);
    };
    check();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready || !ref.current || !CLIENT_ID || !LOGIN_URI) return;
    // Redirect mode: clicking the button takes the user to accounts.google.com,
    // and after they pick an account Google POSTs the credential as a form to
    // LOGIN_URI. The backend (Hono) verifies, issues a session JWT, and 302s
    // them to /auth/callback#token=… The popup-based JS callback below is
    // unused in this mode but the type still requires the field be reachable.
    window.google!.accounts.id.initialize({
      client_id: CLIENT_ID,
      ux_mode: "redirect",
      login_uri: LOGIN_URI,
    });
    window.google!.accounts.id.renderButton(ref.current, {
      theme: "outline",
      size: "large",
      text: "continue_with",
      shape: "pill",
      width,
    });
  }, [ready, width]);

  if (!CLIENT_ID || !LOGIN_URI) {
    if (import.meta.env.DEV) {
      console.warn("GoogleLoginButton: VITE_GOOGLE_CLIENT_ID or VITE_API_URL is not set");
    }
    return null;
  }

  return <div ref={ref} className="flex justify-center" />;
}
